// Prep pack panel — runs the `interview_prep` job for a given session and
// renders the streamed markdown output. Extracted from the old inline
// version in Interview.tsx so the new Interview Session detail page can
// embed it as a tab body.
//
// localStorage key: nice2job.job.interview-prep-{sessionId} (managed by
// useJobRunner). KEEP THIS KEY STABLE — users may have in-flight jobs
// from the previous build.

import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Copy, Trash2, X } from 'lucide-react';
import Progress from '../ui/Progress';
import { apiDelete, apiGet } from '../../lib/api';
import { useJobRunner } from '../../lib/useJobRunner';
import { cleanLLMOutput } from '../../lib/clean-llm-output';
import type { Application } from '../../lib/types';

interface PrepArtifact {
  file: string;
  kind: 'research' | 'prep' | 'other';
  company_slug: string | null;
}
interface PrepListResponse { total: number; items: PrepArtifact[] }

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function PrepPanel({
  session,
  sessionId,
  stage,
}: {
  session: Application;
  sessionId: string;
  stage?: string;
}) {
  // Reattach: interview_prep payload carries `application_id` = sessionId
  // (see start() below), so two devices viewing the same session see
  // the same in-flight prep run.
  const runner = useJobRunner(`interview-prep-${sessionId}`, {
    reattach: {
      cmd: 'interview_prep',
      match: (p) => !!sessionId && String(p.application_id || '') === sessionId,
    },
  });
  const cleanLog = useMemo(() => cleanLLMOutput(runner.log || ''), [runner.log]);

  // Find any saved prep-pack file on disk for this company (so we can offer
  // delete). interview_prep cmd writes to `interview-prep/{slug}-{role}.md`;
  // the list endpoint tags those as kind='other' (research files use a
  // strict {slug}-research-{date}.md pattern).
  const slug = companySlug(session.company);
  const qc = useQueryClient();
  const prepListQ = useQuery({
    queryKey: ['interview-prep-list', slug],
    queryFn: () => apiGet<PrepListResponse>(`/api/interview-prep/list?company_slug=${encodeURIComponent(slug)}`),
    staleTime: 30_000,
  });
  const savedPrepFiles = useMemo(
    () => (prepListQ.data?.items ?? []).filter((i) => i.kind !== 'research'),
    [prepListQ.data],
  );

  // Armed delete (4s window). Holds the filename being armed.
  const [armed, setArmed] = useState<string | null>(null);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(null), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const deleteFile = async (file: string) => {
    if (armed !== file) { setArmed(file); return; }
    setArmed(null);
    try {
      await apiDelete(`/api/interview-prep/${encodeURIComponent(file)}`);
      qc.invalidateQueries({ queryKey: ['interview-prep-list'] });
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const start = () => {
    if (runner.isRunning) return;
    runner.startJob('interview_prep', {
      company: session.company,
      role: session.role,
      application_id: sessionId,
      stage: stage || session.stage,
    });
  };

  const copyAll = () => {
    navigator.clipboard?.writeText(cleanLog).catch(() => {});
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--ink3)', flex: 1 }}>
          Generates a full prep pack: process overview, 13+ likely questions (technical / behavioral /
          role-specific / red flags), story-bank mapping, and company signals to reference.
        </div>
        <button
          className="btn accent"
          onClick={start}
          disabled={runner.isRunning}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Sparkles size={13} /> {runner.isRunning ? 'Generating…' : cleanLog ? 'Re-generate' : 'Generate prep pack'}
        </button>
      </div>

      {runner.isActive && (
        <div style={{ marginBottom: 12 }}>
          <Progress value={Math.min(95, runner.log.length / 60)} />
          <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 4 }}>
            {runner.status} · {runner.log.length.toLocaleString()} chars
            {runner.status === 'done' && (
              <>
                {' · '}
                <button className="btn ghost" onClick={copyAll} style={{ fontSize: 10.5, padding: '1px 6px' }}>
                  <Copy size={10} /> Copy all
                </button>
                {' · '}
                <button className="btn ghost" onClick={runner.dismiss} style={{ fontSize: 10.5, padding: '1px 6px' }}>
                  Clear
                </button>
              </>
            )}
            {runner.isRunning && (
              <>
                {' · '}
                <button className="btn ghost" onClick={runner.cancel} style={{ fontSize: 10.5, padding: '1px 6px', color: '#b43a3a' }}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {runner.errorLine && (
        <div style={{ padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)', marginBottom: 10 }}>
          {runner.errorLine}
        </div>
      )}

      {/* Saved prep-pack files on disk for this company. Each row: filename
          + armed × delete. Hits DELETE /api/interview-prep/:filename. */}
      {savedPrepFiles.length > 0 && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
          <div className="tab" style={{ fontSize: 10.5, marginBottom: 6 }}>
            Saved on disk ({savedPrepFiles.length})
          </div>
          {savedPrepFiles.map((f) => (
            <div key={f.file} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--ink2)', padding: '3px 0' }}>
              <span style={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                interview-prep/{f.file}
              </span>
              <button
                className="btn ghost"
                onClick={() => deleteFile(f.file)}
                title={
                  armed === f.file
                    ? 'Click again to permanently delete this prep pack file'
                    : 'Delete this saved prep pack from disk'
                }
                style={{
                  fontSize: 10.5,
                  padding: armed === f.file ? '3px 8px' : '3px 6px',
                  color: armed === f.file ? '#fff' : '#b43a3a',
                  background: armed === f.file ? '#b43a3a' : 'transparent',
                  border: armed === f.file ? '1px solid #b43a3a' : undefined,
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                }}
              >
                {armed === f.file
                  ? <><X size={10} /> Sure?</>
                  : <Trash2 size={10} />}
              </button>
            </div>
          ))}
        </div>
      )}

      {cleanLog ? (
        <div className="report-md" style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', maxHeight: 520, overflowY: 'auto', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLog}</ReactMarkdown>
        </div>
      ) : !runner.isRunning && (
        <div className="empty" style={{ padding: '20px 12px' }}>
          <span className="serif">No prep pack yet.</span>
          Click Generate to draft one. Takes ~60–90s.
        </div>
      )}
    </div>
  );
}
